<template>
  <div class="omashu-info">

    <!-- 1. Intro -->
    <section class="expo-section section-intro">
      <div class="container">
        <span class="eyebrow archivo-semibold">{{ $t('exposicoesPage.omashuExpress.eyebrowProject') }}</span>
        <h1 class="expo-title">
          <span class="expo-title-pre">{{ $t('exposicoesPage.omashuExpress.titlePart1') }}</span>
          <span class="expo-title-main">{{ $t('exposicoesPage.omashuExpress.titlePart2') }}</span>
          <span class="expo-title-sub">{{ $t('exposicoesPage.omashuExpress.titlePart3') }}</span>
        </h1>
        <p class="lead inter-regular">{{ $t('exposicoesPage.omashuExpress.lead') }}</p>

        <div class="stats-row">
          <div v-for="stat in stats" :key="stat" class="stat">
            <span class="stat-value bebas-neue-regular">{{ $t(`exposicoesPage.omashuExpress.stats.${stat}Value`) }}</span>
            <span class="stat-label archivo-semibold">{{ $t(`exposicoesPage.omashuExpress.stats.${stat}`) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. Engenharia -->
    <section class="expo-section section-light">
      <div class="container">
        <span class="eyebrow eyebrow-dark archivo-semibold">{{ $t('exposicoesPage.omashuExpress.eyebrowEngineering') }}</span>
        <div class="highlight-grid">
          <article v-for="n in 3" :key="n" class="highlight-card">
            <span class="highlight-index bebas-neue-regular">0{{ n }}</span>
            <h3 class="highlight-title archivo-semibold">{{ $t(`exposicoesPage.omashuExpress.highlight${n}Title`) }}</h3>
            <p class="highlight-text inter-regular">{{ $t(`exposicoesPage.omashuExpress.highlight${n}Text`) }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- 3. História -->
    <section class="expo-section section-deep">
      <div class="container split">
        <div class="split-text">
          <span class="eyebrow archivo-semibold">{{ $t('exposicoesPage.omashuExpress.eyebrowHistory') }}</span>
          <h2 class="section-title bebas-neue-regular">{{ $t('exposicoesPage.omashuExpress.historyTitle') }}</h2>
          <p class="body-text inter-regular">{{ $t('exposicoesPage.omashuExpress.historyParagraph1') }}</p>
          <p class="body-text inter-regular">{{ $t('exposicoesPage.omashuExpress.historyParagraph2') }}</p>
        </div>
        <figure class="photo photo-tilt">
          <img :src="superJetSrc" :alt="$t('exposicoesPage.omashuExpress.superJetAlt')" loading="lazy" />
          <figcaption class="caption inter-regular">{{ $t('exposicoesPage.omashuExpress.superJetCaption') }}</figcaption>
        </figure>
      </div>
    </section>

    <!-- 4. Construção -->
    <section class="expo-section section-blue">
      <div class="container">
        <span class="eyebrow eyebrow-on-blue archivo-semibold">{{ $t('exposicoesPage.omashuExpress.eyebrowBuild') }}</span>
        <h2 class="section-title bebas-neue-regular">{{ $t('exposicoesPage.omashuExpress.buildTitle') }}</h2>
        <p class="body-text body-text-on-blue inter-regular">{{ $t('exposicoesPage.omashuExpress.buildParagraph') }}</p>

        <div class="gallery">
          <figure v-for="(photo, i) in gallery" :key="i" class="photo gallery-item" :class="{ 'gallery-wide': i === 1 }">
            <img :src="photo" :alt="$t(`exposicoesPage.omashuExpress.galleryAlt${i + 1}`)" loading="lazy" />
            <figcaption class="caption caption-on-blue inter-regular">{{ $t(`exposicoesPage.omashuExpress.galleryCaption${i + 1}`) }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <!-- 5. Status -->
    <section class="expo-section section-yellow">
      <div class="container status">
        <span class="status-pulse"></span>
        <div>
          <p class="status-label archivo-semibold">{{ $t('exposicoesPage.omashuExpress.statusLabel') }}</p>
          <p class="status-text inter-regular">{{ $t('exposicoesPage.omashuExpress.statusText') }}</p>
        </div>
      </div>
    </section>

    <!-- 6. Equipe -->
    <section class="expo-section section-intro section-team">
      <div class="container">
        <span class="eyebrow archivo-semibold">{{ $t('exposicoesPage.omashuExpress.eyebrowTeam') }}</span>
        <h2 class="section-title bebas-neue-regular">{{ $t('exposicoesPage.omashuExpress.teamTitle') }}</h2>

        <ul class="team-list">
          <li v-for="member in team" :key="member" class="team-member">
            <span class="team-initials bebas-neue-regular">{{ initials(member) }}</span>
            <span class="team-name archivo-semibold">{{ member }}</span>
          </li>
        </ul>
      </div>
    </section>

  </div>
</template>

<script setup>
import superJetSrc from '@/assets/omashu/historia/super-jet-pinfari.jpg'
import buildVeiculoSrc from '@/assets/omashu/historia/construcao-veiculo.jpg'
import buildGeralSrc from '@/assets/omashu/historia/construcao-geral.jpg'
import buildNoturnaSrc from '@/assets/omashu/historia/construcao-noturna.jpg'

const stats = ['height', 'length', 'speed']

const gallery = [
  buildVeiculoSrc,
  buildGeralSrc,
  buildNoturnaSrc,
]

const team = [
  'Gustavo Machado',
  'Caio Neves',
  'Jean Oliveira',
  'Pablo William',
  'Christian Rafael',
  'Wilker Vitor',
]

const initials = (name) => name.split(' ').map(p => p[0]).join('')
</script>

<style scoped>
.omashu-info {
  text-align: left;
}

.expo-section {
  position: relative;
  padding: 96px 0;
}

.container {
  max-width: 1040px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ─── Fundos ──────────────────────────────────────────── */
.section-intro {
  background:
    radial-gradient(ellipse 80% 60% at 85% 0%, rgba(37, 99, 235, 0.28), transparent 70%),
    var(--color-bg-dark);
}

.section-intro:first-child {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 120px;
}

.section-light {
  background: var(--color-light);
}

.section-deep {
  background: #141C27;
}

.section-blue {
  background: linear-gradient(160deg, var(--color-blue) 0%, #1D4FC4 100%);
}

.section-yellow {
  background: var(--color-yellow);
  padding: 48px 0;
}

.section-team {
  background:
    radial-gradient(ellipse 70% 50% at 10% 100%, rgba(251, 191, 36, 0.12), transparent 70%),
    var(--color-bg-dark);
}

/* ─── Eyebrow ─────────────────────────────────────────── */
.eyebrow {
  display: flex;
  align-items: center;
  color: var(--color-yellow);
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.eyebrow::before {
  content: "";
  flex: 0 0 auto;
  width: 24px;
  height: 2px;
  margin-right: 10px;
  background: currentColor;
}

.eyebrow-dark { color: var(--color-blue); }
.eyebrow-on-blue { color: var(--color-yellow); }

/* ─── Título ──────────────────────────────────────────── */
.expo-title {
  display: inline-flex;
  flex-direction: column;
  margin: 0 0 28px;
  cursor: default;
  transform-origin: left center;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.expo-title:hover {
  transform: scale(1.03);
}

.expo-title-pre {
  font-family: "Archivo", sans-serif;
  font-weight: 200;
  font-size: 28px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-light);
  line-height: 1;
}

.expo-title-main {
  font-family: "Bebas Neue", sans-serif;
  font-weight: 400;
  font-size: 112px;
  line-height: 0.92;
  color: var(--color-yellow);
  margin: 6px 0 10px;
  transition: text-shadow 0.35s ease;
}

.expo-title:hover .expo-title-main {
  text-shadow: 0 0 32px rgba(251, 191, 36, 0.35);
}

.expo-title-sub {
  font-family: "Inter", sans-serif;
  font-weight: 300;
  font-size: 18px;
  letter-spacing: 0.04em;
  color: rgba(229, 231, 235, 0.7);
}

/* ─── Textos ──────────────────────────────────────────── */
.lead {
  color: rgba(229, 231, 235, 0.85);
  font-size: 19px;
  font-weight: 300;
  line-height: 1.65;
  margin: 0 0 48px;
  max-width: 720px;
}

.section-title {
  color: var(--color-light);
  font-size: 52px;
  line-height: 1;
  margin: 0 0 20px;
}

.body-text {
  color: rgba(229, 231, 235, 0.78);
  font-size: 16px;
  font-weight: 300;
  line-height: 1.7;
  margin: 0 0 16px;
}

.body-text-on-blue {
  color: rgba(255, 255, 255, 0.88);
  max-width: 720px;
  margin-bottom: 40px;
}

/* ─── Stats ───────────────────────────────────────────── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid rgba(229, 231, 235, 0.15);
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px 20px 0 0;
}

.stat + .stat {
  padding-left: 20px;
  border-left: 1px solid rgba(229, 231, 235, 0.15);
}

.stat-value {
  color: var(--color-light);
  font-size: 44px;
  line-height: 1;
}

.stat-label {
  color: rgba(229, 231, 235, 0.55);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/* ─── Engenharia (fundo claro) ────────────────────────── */
.highlight-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.highlight-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 1px 2px rgba(30, 42, 58, 0.06), 0 12px 32px -12px rgba(30, 42, 58, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.highlight-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 1px 2px rgba(30, 42, 58, 0.06), 0 20px 40px -12px rgba(37, 99, 235, 0.3);
}

.highlight-index {
  display: block;
  color: var(--color-blue);
  font-size: 36px;
  line-height: 1;
  margin-bottom: 14px;
}

.highlight-title {
  color: var(--color-bg-dark);
  font-size: 18px;
  margin: 0 0 10px;
}

.highlight-text {
  color: rgba(30, 42, 58, 0.72);
  font-size: 14px;
  font-weight: 300;
  line-height: 1.65;
  margin: 0;
}

/* ─── História ────────────────────────────────────────── */
.split {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 56px;
  align-items: center;
}

.photo {
  margin: 0;
}

.photo img {
  width: 100%;
  display: block;
  border-radius: 14px;
}

.photo-tilt img {
  transform: rotate(1.5deg);
  box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.6);
  transition: transform 0.4s ease;
}

.photo-tilt:hover img {
  transform: rotate(0deg) scale(1.02);
}

.caption {
  color: rgba(229, 231, 235, 0.5);
  font-size: 12px;
  font-weight: 300;
  line-height: 1.45;
  margin: 12px 2px 0;
}

/* ─── Construção (fundo azul) ─────────────────────────── */
.gallery {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  gap: 16px;
  align-items: start;
}

.gallery-item img {
  height: 240px;
  object-fit: cover;
  box-shadow: 0 16px 32px -12px rgba(10, 20, 60, 0.5);
  transition: transform 0.3s ease;
}

.gallery-wide img {
  height: 300px;
}

.gallery-item:hover img {
  transform: translateY(-4px);
}

.caption-on-blue {
  color: rgba(255, 255, 255, 0.75);
}

/* ─── Status (fundo amarelo) ──────────────────────────── */
.status {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}

.status-pulse {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--color-bg-dark);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(30, 42, 58, 0.45); }
  100% { box-shadow: 0 0 0 14px rgba(30, 42, 58, 0); }
}

.status-label {
  color: var(--color-bg-dark);
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 6px;
}

.status-text {
  color: rgba(30, 42, 58, 0.85);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  margin: 0;
  max-width: 820px;
}

/* ─── Equipe ──────────────────────────────────────────── */
.team-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.team-member {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(229, 231, 235, 0.04);
  border: 1px solid rgba(229, 231, 235, 0.1);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.team-member:hover {
  border-color: rgba(251, 191, 36, 0.5);
  background: rgba(251, 191, 36, 0.06);
}

.team-initials {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--color-blue);
  color: var(--color-light);
  font-size: 18px;
}

.team-name {
  color: var(--color-light);
  font-size: 15px;
}

/* ─── Mobile ──────────────────────────────────────────── */
@media (max-aspect-ratio: 3/4) {
  .expo-section {
    padding: 64px 0;
  }

  .section-intro:first-child {
    padding-top: 96px;
  }

  .container {
    padding: 0 20px;
  }

  .expo-title-pre {
    font-size: 18px;
  }

  .expo-title-main {
    font-size: 64px;
  }

  .expo-title-sub {
    font-size: 15px;
  }

  .lead {
    font-size: 16px;
  }

  .section-title {
    font-size: 40px;
  }

  .stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stat,
  .stat + .stat {
    padding: 20px 12px 16px 0;
    border-left: 0;
  }

  .stat-value {
    font-size: 36px;
  }

  .highlight-grid,
  .split,
  .gallery,
  .team-list {
    grid-template-columns: 1fr;
  }

  .split {
    gap: 32px;
  }

  .gallery-item img,
  .gallery-wide img {
    height: 220px;
  }
}
</style>
